/**
 * PurchasesBlock (04.09, владелец): «покупка оператором чего-либо попадает в
 * дашборд ОО, где он подтверждает эту покупку. Деньги компенсируются вместе с
 * зарплатой, отображаются в расчётках и обозначаются особым цветом» — цвет
 * покупок везде var(--lime). Блок дня рядом с «Допработы и админства»:
 * GET /api/op-purchases?from=d&to=d → строки: превью фото (до 3, защищённый
 * файл → objectURL через apiFileUrl, тап — лайтбокс), оператор, что купил
 * (+комментарий серым), сумма кнопкой (NumPadPop → PUT {amount} — ОО может
 * поправить), статус: ожидает — «✓ Подтвердить» / «✕ Отклонить» (TextPad —
 * причина); подтверждено — lime-метка «кем»; отклонено — приглушённо с
 * причиной и «вернуть в ожидание». Пустой день — блок остаётся с «покупок нет»
 * (канон 02.09: блоки не исчезают). Над списком — «Ожидают подтверждения на
 * других днях: N» из GET /pending-count минус ожидающие этого дня.
 * 04.09 (ревью): (а) busyIds — пока идёт PUT, кнопки строки disabled, повтор
 * тапа игнорируется (двойной PUT = два TG-вердикта); (б) список привязан к
 * дате: при смене дня строки прошлого дня не висят до ответа, устаревший
 * запрос отменяем (AbortController); (в) после ошибки PUT (404 — оператор уже
 * удалил) список перечитываем, чтобы не осталась призрачная строка.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiFileUrl } from "../../api/client";
import { NumPadPop, TextPad } from "../../components/shared";
import { fmtDateApi } from "../../utils/date";
import css from "./dashboard.module.css";

type PStatus = "pending" | "approved" | "rejected";
interface PPhoto { id: number; name?: string | null }
interface Purchase {
  id: number; operator_id: string; operator_name?: string | null; date: string; amount: number; item: string;
  consumable_id?: string | null; comment?: string | null; status: PStatus;
  approved_by?: string | null; approved_by_name?: string | null; approved_at?: string | null;
  reject_reason?: string | null; created_at?: string | null; photos?: PPhoto[];
}

const fmtR = (n: number): string => String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
const errText = (e: unknown, fb: string): string => (e instanceof Error && e.message ? e.message : fb);

/** Защищённое фото → objectURL (канон WvShot 27.08): <img> не шлёт Authorization,
 *  качаем сами; URL освобождаем при размонтировании. */
function usePhotoUrl(photoId: number): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true; let u: string | null = null;
    apiFileUrl(`/api/op-purchases/photo/${photoId}`)
      .then((x) => { if (alive) { u = x; setUrl(x); } else URL.revokeObjectURL(x); })
      .catch(() => {});
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [photoId]);
  return url;
}

function PbShot({ p, onOpen }: { p: PPhoto; onOpen: (p: PPhoto) => void }) {
  const url = usePhotoUrl(p.id);
  if (!url) return <span className={css.pbShotStub} />;
  return (
    <button type="button" className={css.pbShot} title={p.name || "фото — тап увеличить"} onClick={() => onOpen(p)}>
      <img src={url} alt={p.name || "фото покупки"} />
    </button>
  );
}

/** Простой лайтбокс на общей подложке .mvOverlay (как MediaViewerOo): тап мимо / Esc — закрыть. */
function PbLightbox({ p, onClose }: { p: PPhoto; onClose: () => void }) {
  const url = usePhotoUrl(p.id);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className={css.mvOverlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div className={`${css.mvFrame} ${css.pbFrame}`} onClick={(e) => e.stopPropagation()}>
        <div className={css.mvHead}>
          <span className={css.mvTitle}>{p.name || "фото покупки"}</span>
          <button type="button" className={css.mvClose} onClick={onClose} aria-label="Закрыть">✕</button>
        </div>
        {url ? <img className={css.mvMedia} src={url} alt="" /> : <div className={css.pbLoading}>загружаю…</div>}
      </div>
    </div>
  );
}

export function PurchasesBlock({ date }: { date: Date }) {
  const d = fmtDateApi(date);
  /* строки хранятся вместе с датой, за которую пришли: другой день → пусто + «загружаю…» */
  const [data, setData] = useState<{ d: string; items: Purchase[] }>({ d: "", items: [] });
  const items = data.d === d ? data.items : [];
  const loading = data.d !== d;
  const [pendingAll, setPendingAll] = useState(0);
  const [err, setErr] = useState("");
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  const [busyIds, setBusyIds] = useState<number[]>([]);
  const busyRef = useRef(new Set<number>()); // синхронный гейт против двойного тапа до ререндера
  const [pad, setPad] = useState<null | { anchor: HTMLElement; p: Purchase }>(null);
  const [rejectFor, setRejectFor] = useState<Purchase | null>(null);
  const [view, setView] = useState<PPhoto | null>(null);
  const closeView = useCallback(() => setView(null), []);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    api<{ results?: Purchase[] }>(`/api/op-purchases?from=${d}&to=${d}`, { signal: ac.signal })
      .then((r) => { if (alive) { setData({ d, items: r.results ?? [] }); setErr(""); } })
      .catch((e: unknown) => {
        if (!alive) return;
        setData((x) => (x.d === d ? x : { d, items: [] })); // снять «загружаю…», строки того же дня оставить
        setErr(errText(e, "Не удалось загрузить покупки"));
      });
    /* бейдж ОО: pending по всем дням; без assignments:write — 403, молча 0 */
    api<{ count?: number }>("/api/op-purchases/pending-count")
      .then((r) => { if (alive) setPendingAll(Number(r.count) || 0); })
      .catch(() => {});
    return () => { alive = false; ac.abort(); };
  }, [d, tick]);

  const put = useCallback(async (id: number, body: Record<string, unknown>) => {
    if (busyRef.current.has(id)) return;
    busyRef.current.add(id); setBusyIds((s) => [...s, id]);
    try { await api(`/api/op-purchases/${id}`, { method: "PUT", body }); setErr(""); }
    catch (e: unknown) { setErr(errText(e, "Не сохранилось")); }
    finally { busyRef.current.delete(id); setBusyIds((s) => s.filter((x) => x !== id)); reload(); }
  }, [reload]);
  const setAmount = (p: Purchase | undefined, v: number) => { if (p && v >= 1 && Math.round(v) !== p.amount) void put(p.id, { amount: Math.round(v) }); };

  const pendingHere = items.filter((p) => p.status === "pending").length;
  const otherDays = Math.max(0, pendingAll - pendingHere);
  const approvedSum = items.filter((p) => p.status === "approved").reduce((a, p) => a + (p.amount || 0), 0);

  return (
    <section className={`${css.dwBlock} ${css.pbBlock}`}>
      <div className={css.detH}>
        Покупки{pendingHere ? ` · ${pendingHere} на подтверждение` : ""}{approvedSum > 0 ? ` · ₽${fmtR(approvedSum)} к компенсации` : ""}
      </div>
      {otherDays > 0 ? <div className={css.pbOther}>Ожидают подтверждения на других днях: {otherDays}</div> : null}
      {loading ? <div className={css.dwNone}>загружаю…</div> : !items.length ? <div className={css.dwNone}>покупок нет</div> : null}
      {items.map((p) => {
        const photos = p.photos ?? [];
        const busy = busyIds.includes(p.id);
        return (
          <div key={p.id} className={`${css.pbRow} ${p.status === "rejected" ? css.pbRej : ""}`}>
            <div className={css.pbShots}>
              {photos.length ? photos.slice(0, 3).map((ph) => <PbShot key={ph.id} p={ph} onOpen={setView} />) : <span className={css.pbNoShot} title="без фото">—</span>}
              {photos.length > 3 ? <span className={css.pbMore}>+{photos.length - 3}</span> : null}
            </div>
            <div className={css.pbMain}>
              <div className={css.pbLine}>
                <span className={css.dwNm}>{p.operator_name || p.operator_id}</span>
                <span className={css.pbWhat} title={p.item}>{p.item}</span>
                <button type="button" className={css.pbSum} title="Сумма — тап поправить" disabled={busy} onClick={(ev) => setPad({ anchor: ev.currentTarget, p })}>
                  ₽{fmtR(p.amount)}
                </button>
              </div>
              {p.comment ? <div className={css.pbCmt}>{p.comment}</div> : null}
              {p.status === "pending" ? (
                <div className={css.pbActs}>
                  <button type="button" className={`${css.pbAct} ${css.pbOk}`} disabled={busy} onClick={() => void put(p.id, { status: "approved" })}>{busy ? "…" : "✓ Подтвердить"}</button>
                  <button type="button" className={`${css.pbAct} ${css.pbNo}`} disabled={busy} onClick={() => setRejectFor(p)}>✕ Отклонить</button>
                </div>
              ) : p.status === "approved" ? (
                <div className={css.pbTag}>подтверждено{p.approved_by_name ? ` · ${p.approved_by_name}` : ""}</div>
              ) : (
                <div className={css.pbActs}>
                  <span className={css.pbReason}>отклонено{p.reject_reason ? ` · ${p.reject_reason}` : ""}</span>
                  <button type="button" className={`${css.pbAct} ${css.pbBack}`} disabled={busy} onClick={() => void put(p.id, { status: "pending" })}>{busy ? "…" : "↩ вернуть в ожидание"}</button>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {err ? <div className={css.pbErr}>{err}</div> : null}
      <NumPadPop
        open={!!pad} anchor={pad?.anchor ?? null} label={`Сумма покупки · ${pad?.p.operator_name ?? ""}`}
        mode="money" value={pad?.p.amount ?? 0} max={999999}
        /* onCommit зовётся ПОСЛЕ onClose — pad берём из замыкания рендера, как в DayWork */
        onCommit={(v) => setAmount(pad?.p, v)} onClose={() => setPad(null)}
        extraKeys={[{ label: "Записать", onPress: (v) => { const p = pad?.p; setPad(null); setAmount(p, v); } }]}
      />
      <TextPad
        open={!!rejectFor} label={`Причина отклонения · ${rejectFor?.operator_name ?? ""}`} placeholder="Почему не компенсируем…" maxLength={200}
        onCommit={(v) => { const p = rejectFor; const t = v.trim(); if (p) void put(p.id, { status: "rejected", ...(t ? { reject_reason: t } : {}) }); }}
        onClose={() => setRejectFor(null)}
      />
      {view ? <PbLightbox p={view} onClose={closeView} /> : null}
    </section>
  );
}
